import React, { Suspense, useEffect, useRef } from "react"
import { Panel, PanelGroup, PanelResizeHandle, type ImperativePanelHandle } from "react-resizable-panels"
import { GripVertical } from "lucide-react"
import type { OpenTab } from "../features/document-tabs"
import { DocumentContent } from "../features/document-content"
import { HomeTab } from "../features/home-tab"
import { DocumentDeepLinkLoadingShell } from "./document-deep-link-loading-shell"
import { isDocumentDeepLinkRestorePending } from "@/lib/document-deep-link-pending"
import { WorkspaceHomeDashboard } from "../features/workspace-home-dashboard"
import { DeskLandingDashboard } from "../features/desk-landing-dashboard"
import { InboxPage, InboxThreadMainTab } from "@/components/features/inbox"
import { FlowStudioMain } from "@/components/features/flows/flow-studio-main"
import { flowStudioSurfaceFromTabId } from "@/components/features/flows/flow-studio-events"
import { ChatMainTab } from "@/components/features/chats/ChatMainTab"
import { DocumentInfoPane } from "../features/document-info-pane"
import {
  DocumentInfoDock,
  type InfoDockMode,
} from "../features/document-info-dock"
import { SecondaryPanel } from "../layout/secondary-panel"
import { PANEL_SIZES } from "../../lib/panel-sizes"
import { DiffViewer } from "../features/diff-viewer"
import { DocumentToc, CurrentSectionPill } from "../features/document-toc"
import type { DocHeading } from "@/lib/heading-utils"
import { GoogleDrivePreviewViewer } from "@/components/integrations/google-workspace/GoogleDrivePreviewViewer"
import { DrivePreviewInfoPane } from "@/components/integrations/google-workspace/drive-preview-info-pane"
import { NextcloudDocumentInfoPane } from "@/components/integrations/nextcloud/NextcloudDocumentInfoPane"
import { CloudDispatchBanner } from "@/components/features/agent/cloud-dispatch-banner"
import { KnowledgeHubStatusStrip } from "@/components/features/knowledge-hub/knowledge-hub-status-strip"
import { SkillDocumentBadge } from "@/components/features/skills/skill-document-badge"
import { PlatformSkillPreview } from "@/components/features/skills/platform-skill-preview"
import { platformSkillSlugFromTabId } from "@/lib/open-platform-skill"
import { DocumentVersionHistoryPanel } from "@/components/features/document-version-history-panel"
import { resolveDocumentRowId } from "@/lib/api"
import { isReviewEnabled } from "@/lib/knowledge-hub-utils"
import { fetchWithRetry } from "./resizable-layout.utils"
import { constructBasecampUrl, getCurrentTabSlotId, isMarkdownProductBoardFilePath, isPathKeyedUnresolvedDocumentId, openTargetForUnresolvedPath, resolveShellInfoDockKind } from "./document-viewer.utils"
import { resolveNavigationTarget } from "@/lib/api/documents"
import { testIds } from "@/lib/test-ids"
import { resolveStoragePathForFetch } from "@/lib/app-routes"
import { peopleIndexIsPreviousEntry } from "@/lib/person-view"
import { lazyWithReload } from "@/lib/chunk-load-recovery"
import { LazyViewFallback } from "./document-viewer-lazy-fallback"
import type { DocumentSaveState, DocumentType, ViewMode } from "./document-viewer.types"
import type { Workspace } from "@/providers/workspace-provider"

/** KH / skill chrome strips above the reading column (py-1.5 + h-7 control + border). */
const DOC_CHROME_STRIP_OFFSET_PX = 44

const BasecampTodoListViewer = lazyWithReload(() =>
  import("../integrations/basecamp/basecamp-todo-list-viewer").then((m) => ({
    default: m.BasecampTodoListViewer,
  }))
)
const BasecampTodoViewer = lazyWithReload(() =>
  import("../integrations/basecamp/basecamp-todo-viewer").then((m) => ({
    default: m.BasecampTodoViewer,
  }))
)
const BasecampTriagePanel = lazyWithReload(() =>
  import("../integrations/basecamp/basecamp-triage-panel").then((m) => ({
    default: m.BasecampTriagePanel,
  }))
)
const ProductBoard = lazyWithReload(() =>
  import("../features/product-board").then((m) => ({ default: m.ProductBoard }))
)
const KnowledgeHubQueueView = lazyWithReload(() =>
  import("../features/knowledge-hub/knowledge-hub-queue-view").then((m) => ({
    default: m.KnowledgeHubQueueView,
  }))
)
const DriveFolderView = lazyWithReload(() =>
  import("@/components/integrations/google-workspace/drive-folder-view").then((m) => ({
    default: m.DriveFolderView,
  }))
)
const NextcloudBrowseView = lazyWithReload(() =>
  import("@/components/integrations/nextcloud/NextcloudBrowseView").then((m) => ({
    default: m.NextcloudBrowseView,
  }))
)
const BasecampTodoEditPane = lazyWithReload(() =>
  import("../integrations/basecamp/basecamp-todo-edit-pane").then((m) => ({
    default: m.BasecampTodoEditPane,
  }))
)
const BasecampProjectInfoPane = lazyWithReload(() =>
  import("../integrations/basecamp/basecamp-project-info-pane").then((m) => ({
    default: m.BasecampProjectInfoPane,
  }))
)
const GraphVisualization = lazyWithReload(() =>
  import("./views/graph-visualization").then((m) => ({ default: m.GraphVisualization }))
)
const AdvancedSearchView = lazyWithReload(() =>
  import("./views/advanced-search-view").then((m) => ({ default: m.AdvancedSearchView }))
)
const PersonView = lazyWithReload(() =>
  import("../features/person-view").then((m) => ({ default: m.PersonView }))
)
const PeopleIndex = lazyWithReload(() =>
  import("../features/people-index").then((m) => ({ default: m.PeopleIndex }))
)

type BasecampProject = {
  id?: string | number
  name?: string
}

type ConfiguredIntegration = {
  id: string
  MainView?: React.ComponentType<{ initialData?: OpenTab["integrationData"] }>
}

type DocumentViewerMainContentProps = {
  openTabs: OpenTab[]
  activeTabId: string
  activeTab: OpenTab | undefined
  viewMode: ViewMode
  workspaces: Workspace[]
  basecampProjects: BasecampProject[] | undefined
  activeWorkspaceIds: string[]
  configuredIntegrations: ConfiguredIntegration[]
  showInfoPane: boolean
  infoDockMode: InfoDockMode
  setInfoDockMode: React.Dispatch<React.SetStateAction<InfoDockMode>>
  infoPanelRef: React.RefObject<ImperativePanelHandle | null>
  documentPanelSizes: number[]
  setDocumentPanelSizes: (sizes: number[]) => void
  documentFrontmatter: Record<string, any> | null
  setDocumentFrontmatter: React.Dispatch<React.SetStateAction<Record<string, any> | null>>
  documentBodyForChangelog: string
  setDocumentBodyForChangelog: React.Dispatch<React.SetStateAction<string>>
  showDocumentToc: boolean
  tocOpen: boolean
  setTocOpen: React.Dispatch<React.SetStateAction<boolean>>
  tocHeadings: DocHeading[]
  tocContentRef: React.RefObject<HTMLDivElement | null>
  scrollElRef: React.RefObject<HTMLElement>
  markdownBodyViewMode: "wysiwyg" | "source"
  setMarkdownBodyViewMode: React.Dispatch<React.SetStateAction<"wysiwyg" | "source">>
  documentSaveState: DocumentSaveState
  cloudDispatch: any
  isDispatchingToCloud: boolean
  setWatchLiveAgentId: React.Dispatch<React.SetStateAction<string | null>>
  searchBoostOpenTabIds: string[]
  searchBoostRecentlyViewedIds: string[]
  handleDocumentClickWithEvent: (documentId: string, eventOrBoolean?: React.MouseEvent | boolean) => Promise<void>
  handleDocumentClick: (documentId: string, openInNewTab?: boolean) => Promise<void>
  handleNewDocument: () => Promise<void>
  onFolderClick?: (
    folderPath: string,
    skipTabManagement?: boolean,
    historySlotId?: string,
  ) => void
  parentDocumentClick?: (documentId: string, event?: React.MouseEvent) => Promise<void>
  parentGoBack?: () => void
  parentGoForward?: () => void
  handleGoBack: () => void
  handleGoForward: () => void
  navigation: {
    canGoBack: (tabSlotId: string) => boolean
    canGoForward: (tabSlotId: string) => boolean
    navigate: (
      tabId: string,
      entry: {
        documentId: string | null
        viewMode: ViewMode
        tabType?: string
        searchQuery?: string
        searchPath?: string
      },
      caller?: string,
    ) => void
    getCurrentEntry: (tabId: string) => {
      searchQuery?: string
      searchPath?: string
    } | null
    tabNavigationState?: Record<string, {
      history: Array<{ documentId: string | null }>
      currentIndex: number
    }>
  }
  onUrlChange?: (contentId: string, params: import("@/hooks/use-url-sync").UrlChangeParams) => void
  markProgrammaticNavigation: () => void
  setOpenTabs: React.Dispatch<React.SetStateAction<OpenTab[]>>
  setActiveTabId: (id: string) => void
  handleAIDocumentSave: (content: string, type: DocumentType, title: string) => void
  handleAIDocumentCancel: () => void
  handleSendToChat: (text: string, documentId: string) => void
  handleInternalLinkClick: (href: string, event?: MouseEvent) => void | Promise<void>
  handleManualSave: () => void
  completeManualSave: () => void
  handleFrontmatterChange: (frontmatter: Record<string, any> | null, documentId?: string) => void
  onDocumentMetaSync?: (documentId: string, meta: { title?: string; filePath?: string | null }) => void
  handleDeleteClick: () => void
  handleCloseInfoPane: () => void
  setBasecampTodoTitle: React.Dispatch<React.SetStateAction<string | null>>
  setBasecampTodoTitleVisible: React.Dispatch<React.SetStateAction<boolean>>
  driveSyncActivity?: 'idle' | 'working' | 'success' | 'error' | 'conflict'
  driveSyncLabel?: string | null
  driveGdocId?: string | null
  driveOrganizationId?: string | null
  driveLastSynced?: string | null
  driveLastPushed?: string | null
  driveLastPulled?: string | null
  driveKeepSynced?: boolean
}

function isNormalDocumentTab(tab: OpenTab) {
  return (
    tab.id !== "graph" &&
    tab.id !== "edit-history" &&
    tab.type !== "edit-history" &&
    tab.id !== "view:edit-history" &&
    tab.type !== "home" &&
    tab.type !== "inbox" &&
    tab.type !== "inbox-thread" &&
    tab.type !== "chat-conversation" &&
    tab.type !== "explorer" &&
    !isPathKeyedUnresolvedDocumentId(tab.id) &&
    tab.type !== "workspace-home" &&
    tab.type !== "desk-home" &&
    tab.type !== "integration-basecamp" &&
    tab.type !== "integration-basecamp-todo" &&
    tab.type !== "integration-basecamp-todo-list" &&
    tab.type !== "integration-basecamp-triage" &&
    tab.type !== "integration-voice-memos" &&
    tab.type !== "integration-product-board" &&
    tab.type !== "integration-knowledge-hub" &&
    tab.type !== "integration-google-drive" &&
    tab.type !== "integration-google-drive-preview" &&
    tab.type !== "integration-nextcloud" &&
    tab.type !== "platform-skill" &&
    tab.type !== "graph-view" &&
    tab.type !== "search-view" &&
    tab.type !== "person-view" &&
    tab.type !== "flow-index"
  )
}

function FolderDeepLinkToExplorer({
  tab,
  onUrlChange,
  workspaces,
}: {
  tab: OpenTab
  onUrlChange?: DocumentViewerMainContentProps['onUrlChange']
  workspaces: Workspace[]
}) {
  const startedRef = useRef(false)
  const workspacesRef = useRef(workspaces)
  workspacesRef.current = workspaces
  useEffect(() => {
    if (startedRef.current || !onUrlChange) return
    if (!isPathKeyedUnresolvedDocumentId(tab.id)) return
    startedRef.current = true
    let cancelled = false
    const raw = tab.filePath || tab.id
    const storagePath = resolveStoragePathForFetch(
      raw,
      workspacesRef.current.map((w) => ({
        id: w.id,
        slug: w.slug,
        contentRoot: w.contentRoot,
      })),
    )
    resolveNavigationTarget(storagePath)
      .then((resolved) => {
        if (cancelled) return
        const target = openTargetForUnresolvedPath(resolved.kind, {
          id: resolved.kind === 'document' ? resolved.id : undefined,
          path: resolved.kind === 'folder' ? resolved.path : storagePath,
        })
        if (!target) return
        if (target.contentId === 'list') {
          onUrlChange('list', target.params)
          return
        }
        onUrlChange(target.contentId, target.params)
      })
      .catch(() => {
        // Leave the shell. A reload still resolves the note.
      })
    return () => {
      cancelled = true
    }
  }, [onUrlChange, tab.filePath, tab.id])
  return <DocumentDeepLinkLoadingShell pathOrId={tab.filePath || tab.id} />
}

/** Main active-tab router and secondary panel for DocumentViewer. */
export function DocumentViewerMainContent({
  openTabs,
  activeTabId,
  activeTab,
  viewMode,
  workspaces,
  basecampProjects,
  activeWorkspaceIds,
  configuredIntegrations,
  showInfoPane,
  infoDockMode,
  setInfoDockMode,
  infoPanelRef,
  documentPanelSizes,
  setDocumentPanelSizes,
  documentFrontmatter,
  setDocumentFrontmatter,
  documentBodyForChangelog,
  setDocumentBodyForChangelog,
  showDocumentToc,
  tocOpen,
  setTocOpen,
  tocHeadings,
  tocContentRef,
  scrollElRef,
  markdownBodyViewMode,
  setMarkdownBodyViewMode,
  documentSaveState,
  cloudDispatch,
  isDispatchingToCloud,
  setWatchLiveAgentId,
  searchBoostOpenTabIds,
  searchBoostRecentlyViewedIds,
  handleDocumentClickWithEvent,
  handleDocumentClick,
  handleNewDocument,
  onFolderClick,
  parentDocumentClick,
  parentGoBack,
  parentGoForward,
  handleGoBack,
  handleGoForward,
  navigation,
  onUrlChange,
  markProgrammaticNavigation,
  setOpenTabs,
  setActiveTabId,
  handleAIDocumentSave,
  handleAIDocumentCancel,
  handleSendToChat,
  handleInternalLinkClick,
  handleManualSave,
  completeManualSave,
  handleFrontmatterChange,
  onDocumentMetaSync,
  handleDeleteClick,
  handleCloseInfoPane,
  setBasecampTodoTitle,
  setBasecampTodoTitleVisible,
  driveSyncActivity,
  driveSyncLabel,
  driveGdocId,
  driveOrganizationId,
  driveLastSynced,
  driveLastPushed,
  driveLastPulled,
  driveKeepSynced,
}: DocumentViewerMainContentProps) {
  const getCurrentTabSlotIdHelper = () => getCurrentTabSlotId(activeTabId, openTabs)
  /** When Visual-mode change banner is up, offset the TOC pill below it. */
  const [changeBannerVisible, setChangeBannerVisible] = React.useState(false)

  React.useEffect(() => {
    setChangeBannerVisible(false)
  }, [activeTabId])

  const renderTabsContent = () => (
    openTabs
      .filter((tab, index, list) =>
        tab.id !== "list" && list.findLastIndex((other) => other.id === tab.id) === index)
      .map(tab => {
        const isActive = tab.id === activeTabId
        const tabPathKey =
          typeof tab.filePath === "string"
            ? tab.filePath
            : typeof tab.id === "string" && tab.id.includes("/")
              ? tab.id
              : null
        const isProductBoardDocument =
          isActive &&
          (tab.type === "note" || tab.type === "work-spec" || tab.type === "voice-memo") &&
          (documentFrontmatter?.type === "product-board" ||
            (tabPathKey != null && isMarkdownProductBoardFilePath(tabPathKey)))
        const personSlotId = tab.tabSlotId || 'tab-main'
        const personSlot = navigation.tabNavigationState?.[personSlotId]

        return (
          <div
            key={tab.id}
            className="absolute inset-0 h-full w-full"
            style={{
              display: isActive ? "block" : "none",
              zIndex: isActive ? 1 : 0,
              pointerEvents: isActive ? "auto" : "none",
            }}
          >
            {tab.type === "home" ? (
              isDocumentDeepLinkRestorePending() ? (
                <DocumentDeepLinkLoadingShell />
              ) : (
                <HomeTab
                  onDocumentClick={handleDocumentClickWithEvent}
                  onNewDocument={handleNewDocument}
                  onSearch={() => {
                    window.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true }))
                  }}
                  onBrowseWorkspace={(documentsRootPath) => {
                    if (documentsRootPath) {
                      onFolderClick?.(documentsRootPath)
                    }
                  }}
                  onInboxItemActivate={(item) => {
                    const docId = item.actions?.find((a) => a.openDocumentId)?.openDocumentId
                    if (docId) {
                      void handleDocumentClick(docId)
                      return
                    }
                    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(item.id)) {
                      const tagIds = item.taggedOrganizationIds ?? []
                      const workspaceId =
                        tagIds[0] ??
                        (typeof item.meta?.workspaceId === "string"
                          ? item.meta.workspaceId
                          : undefined)
                      window.dispatchEvent(
                        new CustomEvent("open-inbox-thread-main", {
                          detail: {
                            threadId: item.id,
                            title: item.title,
                            workspaceId,
                          },
                        }),
                      )
                    }
                  }}
                  onChatThreadClick={(threadId, title, event) => {
                    window.dispatchEvent(
                      new CustomEvent("open-chat-conversation-main", {
                        detail: { threadId, title, clickEvent: event },
                      }),
                    )
                  }}
                  onFlowClick={(flow) => {
                    if (flow.file_path) {
                      void handleDocumentClick(flow.file_path)
                    }
                  }}
                />
              )
            ) : null}

            {tab.type === "inbox" ? <InboxPage /> : null}

            {tab.type === "flow-index" ? (
              <FlowStudioMain
                surface={flowStudioSurfaceFromTabId(tab.id) ?? "pulse"}
                onOpenDocument={handleDocumentClickWithEvent}
              />
            ) : null}

            {tab.type === "inbox-thread" && tab.inboxThreadId ? (
              <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
                <InboxThreadMainTab threadId={tab.inboxThreadId} />
              </div>
            ) : null}

            {tab.type === "chat-conversation" && tab.chatThreadId ? (
              <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
                <ChatMainTab
                  threadId={tab.chatThreadId}
                  title={tab.title}
                  onDocumentClick={handleDocumentClickWithEvent}
                  onFolderClick={onFolderClick}
                  openTabIds={searchBoostOpenTabIds}
                  recentlyViewedIds={searchBoostRecentlyViewedIds}
                />
              </div>
            ) : null}

            {tab.type === "person-view" && tab.personUserId ? (
              <Suspense fallback={<LazyViewFallback />}>
                <PersonView
                  userId={tab.personUserId}
                  workspaceId={tab.workspaceId}
                  onDisplayName={(name) => setOpenTabs(prev => {
                    const next = prev.map(t => t.id === tab.id && t.title !== name ? { ...t, title: name } : t)
                    return next.some((t, i) => t !== prev[i]) ? next : prev
                  })}
                  onGoBack={parentGoBack || handleGoBack}
                  onGoForward={parentGoForward || handleGoForward}
                  canGoBack={navigation.canGoBack(personSlotId)}
                  canGoForward={navigation.canGoForward(personSlotId)}
                  peopleCrumbIsBack={peopleIndexIsPreviousEntry(
                    personSlot?.history,
                    personSlot?.currentIndex ?? -1,
                  )}
                />
              </Suspense>
            ) : null}

            {tab.type === "person-view" && !tab.personUserId ? (
              <Suspense fallback={<LazyViewFallback />}>
                <PeopleIndex
                  workspaceId={tab.workspaceId}
                  onGoBack={parentGoBack || handleGoBack}
                  onGoForward={parentGoForward || handleGoForward}
                  canGoBack={navigation.canGoBack(personSlotId)}
                  canGoForward={navigation.canGoForward(personSlotId)}
                />
              </Suspense>
            ) : null}

            {tab.type === "search-view" ? (
              <Suspense fallback={<LazyViewFallback />}>
                <AdvancedSearchView
                  initialQuery={tab.initialQuery ?? ""}
                  initialPath={tab.searchPath ?? ""}
                  workspaces={workspaces}
                  forceView={tab.searchNamedView === 'activity' ? 'activity' : undefined}
                  initialUpdatedBy={tab.searchUpdatedBy}
                  initialSemantic={tab.searchSemantic}
                  onSemanticChange={(on) => setOpenTabs(prev =>
                    prev.map(t => t.id === tab.id ? { ...t, searchSemantic: on } : t)
                  )}
                  onQueryChange={(q) => setOpenTabs(prev =>
                    prev.map(t => t.id === tab.id ? { ...t, initialQuery: q } : t)
                  )}
                  onPathChange={(path) => setOpenTabs(prev =>
                    prev.map(t => t.id === tab.id ? { ...t, searchPath: path || undefined } : t)
                  )}
                  onNamedViewChange={(namedView) => setOpenTabs(prev =>
                    prev.map(t =>
                      t.id === tab.id
                        ? {
                            ...t,
                            searchNamedView: namedView,
                            title: namedView === 'activity' ? 'Activity' : 'Search',
                          }
                        : t,
                    )
                  )}
                  onGoBack={parentGoBack || handleGoBack}
                  onGoForward={parentGoForward || handleGoForward}
                  canGoBack={navigation.canGoBack(getCurrentTabSlotIdHelper())}
                  canGoForward={navigation.canGoForward(getCurrentTabSlotIdHelper())}
                  onQueryCommit={(q, path) => {
                    const slot = getCurrentTabSlotIdHelper()
                    const current = navigation.getCurrentEntry(slot)
                    if (
                      (current?.searchQuery ?? '').trim() === q.trim() &&
                      (current?.searchPath ?? '').trim() === (path ?? '').trim()
                    ) {
                      return
                    }
                    navigation.navigate(
                      slot,
                      {
                        documentId: tab.id,
                        viewMode: 'single',
                        tabType: 'search-view',
                        searchQuery: q,
                        searchPath: path || undefined,
                      },
                      'search-query-commit',
                    )
                  }}
                />
              </Suspense>
            ) : null}

            {tab.type === "workspace-home" && tab.workspaceId ? (
              <WorkspaceHomeDashboard
                workspaceId={tab.workspaceId}
                onDocumentClick={handleDocumentClickWithEvent}
                onFolderClick={
                  onFolderClick as (
                    folderPath: string,
                    skipTabManagementOrEvent?: boolean | React.MouseEvent,
                  ) => void
                }
              />
            ) : null}

            {tab.type === "desk-home" && tab.workspaceId && tab.deskSlug ? (
              <DeskLandingDashboard
                workspaceId={tab.workspaceId}
                deskSlug={tab.deskSlug}
                onDocumentClick={handleDocumentClickWithEvent}
                onFolderClick={
                  onFolderClick as (
                    folderPath: string,
                    skipTabManagementOrEvent?: boolean | React.MouseEvent,
                  ) => void
                }
              />
            ) : null}

            {tab.id === "graph" || tab.type === "graph-view" ? (
              <div className="h-full w-full">
                <Suspense fallback={<LazyViewFallback />}>
                  <GraphVisualization onDocumentClick={parentDocumentClick} />
                </Suspense>
              </div>
            ) : null}

            {/* Legacy edit-history tabs: host on Search (Activity named view) */}
            {tab.type === "edit-history" || tab.id === "edit-history" || tab.id === "view:edit-history" ? (
              <Suspense fallback={<LazyViewFallback />}>
                <AdvancedSearchView
                  initialQuery=""
                  workspaces={workspaces}
                  forceView="activity"
                />
              </Suspense>
            ) : null}

            {tab.type === "integration-basecamp-todo-list" ? (
              <div className="h-full min-h-0">
                <Suspense fallback={<LazyViewFallback />}>
                  <BasecampTodoListViewer
                    listId={(tab.integrationData?.listId ?? tab.id.replace("basecamp-todo-list-", "")) || "unknown"}
                    projectId={tab.integrationData?.projectId ?? "unknown"}
                    organizationId={tab.integrationData?.organizationId ?? tab.workspaceId ?? undefined}
                    projectName={
                      tab.integrationData?.projectName ||
                      basecampProjects?.find(p => p.id?.toString() === tab.integrationData?.projectId?.toString())?.name ||
                      undefined
                    }
                    onDocumentClick={handleDocumentClickWithEvent}
                  />
                </Suspense>
              </div>
            ) : null}

            {tab.type === "integration-basecamp-triage" ? (
              <div className="h-full min-h-0">
                <Suspense fallback={<LazyViewFallback />}>
                  <BasecampTriagePanel
                    onOpenBasecampTodo={
                      onUrlChange
                        ? (projectId, todoId, title) => {
                            markProgrammaticNavigation()
                            onUrlChange(`basecamp-todo-${todoId}`, {
                              type: "integration-basecamp-todo",
                              projectId,
                              todoId,
                              todoTitle: title,
                            })
                          }
                        : undefined
                    }
                    onDocumentClick={handleDocumentClickWithEvent}
                  />
                </Suspense>
              </div>
            ) : null}

            {tab.type === "integration-product-board" ? (
              <div className="h-full min-h-0 min-w-0 overflow-x-auto overflow-y-auto">
                <Suspense fallback={<LazyViewFallback />}>
                  <ProductBoard />
                </Suspense>
              </div>
            ) : null}

            {tab.type === "integration-knowledge-hub" ? (
              <div className="h-full min-h-0 min-w-0 overflow-auto">
                <Suspense fallback={<LazyViewFallback />}>
                  <KnowledgeHubQueueView
                    workspaceId={tab.integrationData?.organizationId || tab.workspaceId || ""}
                    statusFilter={tab.integrationData?.statusFilter ?? undefined}
                  />
                </Suspense>
              </div>
            ) : null}

            {tab.type === "integration-nextcloud" ? (
              <div className="h-full min-h-0 min-w-0">
                <Suspense fallback={<LazyViewFallback />}>
                  <NextcloudBrowseView
                    organizationId={tab.integrationData?.organizationId ?? tab.workspaceId ?? undefined}
                    path={tab.integrationData?.nextcloudPath || '/'}
                    kind={tab.integrationData?.nextcloudKind || 'folder'}
                    name={tab.title}
                    fileId={tab.integrationData?.nextcloudFileId}
                    previewPath={tab.integrationData?.nextcloudPreviewPath}
                    surface={tab.integrationData?.nextcloudSurface}
                  />
                </Suspense>
              </div>
            ) : null}

            {tab.type === "integration-google-drive" ? (
              <div className="h-full min-h-0 min-w-0">
                <Suspense fallback={<LazyViewFallback />}>
                  <DriveFolderView
                    organizationId={tab.integrationData?.organizationId ?? tab.workspaceId ?? undefined}
                  />
                </Suspense>
              </div>
            ) : null}

            {tab.type === "integration-google-drive-preview" && tab.integrationData?.driveFileId ? (
              <div className="h-full min-h-0 min-w-0">
                <GoogleDrivePreviewViewer
                  data={{
                    fileId: tab.integrationData.driveFileId,
                    name: tab.title,
                    mimeType: tab.integrationData.driveMimeType ?? "",
                    webViewLink: tab.integrationData.driveWebViewLink,
                    organizationId: tab.integrationData.organizationId ?? tab.workspaceId ?? undefined,
                    importedDocumentId: tab.integrationData.importedDocumentId,
                    isShortcut: tab.integrationData.driveIsShortcut ?? false,
                  }}
                  onOpenDocument={(documentId) => {
                    void handleDocumentClickWithEvent(documentId)
                  }}
                  onReplaceWithDocument={(documentId) => {
                    const slot = tab.tabSlotId ?? `tab-${tab.id}`
                    // Deliberately omit workspaceId/filePath here — the imported document may
                    // belong to a different workspace than the Drive-preview tab it replaces.
                    // The immediate handleDocumentClickWithEvent() call below resolves both
                    // fresh from the document's real path/org via switchToExistingTab(), so
                    // carrying over the old tab's (possibly stale) workspaceId is unnecessary
                    // and was previously causing the tab to resolve to the wrong workspace.
                    setOpenTabs((prev) =>
                      prev.map((t) =>
                        t.id === tab.id
                          ? {
                              id: documentId,
                              title: t.title,
                              type: "note" as const,
                              isDirty: false,
                              tabSlotId: slot,
                            }
                          : t,
                      ),
                    )
                    setActiveTabId(documentId)
                    void handleDocumentClickWithEvent(documentId)
                  }}
                />
              </div>
            ) : null}

            {tab.type === "platform-skill" ? (
              <div className="h-full min-h-0 min-w-0">
                <PlatformSkillPreview
                  slug={platformSkillSlugFromTabId(tab.id)}
                  fallbackName={tab.title}
                />
              </div>
            ) : null}

            {tab.type === "integration-basecamp-todo" ? (
              <div className="h-full min-h-0">
                <Suspense fallback={<LazyViewFallback />}>
                  <BasecampTodoViewer
                    openTabs={openTabs}
                    activeTabId={activeTabId}
                    todoId={tab.integrationData?.todoId || tab.id.replace("basecamp-todo-", "")}
                    projectId={tab.integrationData?.projectId}
                    projectName={tab.integrationData?.projectName || undefined}
                    organizationId={tab.integrationData?.organizationId ?? tab.workspaceId ?? undefined}
                    highlightStepId={tab.integrationData?.highlightStepId}
                    rightPanelExpanded={showInfoPane}
                    onDocumentClick={handleDocumentClickWithEvent}
                    onGoBack={parentGoBack || handleGoBack}
                    onGoForward={parentGoForward || handleGoForward}
                    canGoBack={navigation.canGoBack(getCurrentTabSlotIdHelper())}
                    canGoForward={navigation.canGoForward(getCurrentTabSlotIdHelper())}
                    onTodoLoad={(todo) => {
                      setOpenTabs(prevTabs => prevTabs.map(t => {
                        if (t.id === tab.id && t.integrationData) {
                          return {
                            ...t,
                            title: todo?.title || t.title,
                            integrationData: {
                              ...t.integrationData,
                              listName: todo?.parent?.title,
                              listId: todo?.parent?.id?.toString(),
                              todoData: todo,
                            },
                          }
                        }
                        return t
                      }))
                      if (todo?.title) {
                        setBasecampTodoTitle(todo.title)
                      }
                    }}
                    onTitleVisibilityChange={(isVisible) => {
                      setBasecampTodoTitleVisible(isVisible)
                    }}
                  />
                </Suspense>
              </div>
            ) : null}

            {tab.type === "integration-basecamp" ? (() => {
              const currentIntegration = configuredIntegrations.find(
                i => i.id === tab.integrationData?.integrationId
              )

              return currentIntegration?.MainView ? (
                <div className="h-full min-h-0" key={tab.id || "integration"}>
                  <currentIntegration.MainView
                    key={tab.integrationData?.projectId || tab.id}
                    initialData={tab.integrationData}
                  />
                </div>
              ) : null
            })() : null}

            {tab.type === "integration-voice-memos" ? (() => {
              const voiceMemoIntegration = configuredIntegrations.find(
                i => i.id === (tab.integrationData?.integrationId || "voice-memos")
              )

              if (!voiceMemoIntegration?.MainView) {
                return (
                  <div className="p-6 text-sm text-muted-foreground" key={`${tab.id}-missing`}>
                    Voice Memo integration is not available. Make sure it is enabled in settings.
                  </div>
                )
              }

              return (
                <div className="h-full min-h-0" key={tab.id || "integration-voice-memos"}>
                  <voiceMemoIntegration.MainView initialData={tab.integrationData} />
                </div>
              )
            })() : null}

            {isPathKeyedUnresolvedDocumentId(tab.id) ? (
              <FolderDeepLinkToExplorer
                tab={tab}
                onUrlChange={onUrlChange}
                workspaces={workspaces}
              />
            ) : null}

            {isNormalDocumentTab(tab) ? (
              viewMode === "single" ? (
                <div className="h-full overflow-hidden flex flex-col">
                  <div className="flex-1 overflow-hidden flex flex-row">
                    {showDocumentToc && tocOpen && !isProductBoardDocument && (
                      <DocumentToc
                        markdown=""
                        headings={tocHeadings}
                        contentRef={tocContentRef as React.RefObject<HTMLElement>}
                        scrollRef={scrollElRef}
                        onClose={() => setTocOpen(false)}
                        documentId={tab.id}
                      />
                    )}
                    <div
                      className="flex-1 overflow-hidden relative min-w-0 flex flex-col"
                      data-testid={isActive ? testIds.documentViewer.readingPane : undefined}
                    >
                      {isActive && (cloudDispatch?.agentId || isDispatchingToCloud) && (
                        <CloudDispatchBanner
                          dispatch={cloudDispatch}
                          isDispatching={isDispatchingToCloud}
                          workspaceId={tab.workspaceId}
                          onWatchLive={setWatchLiveAgentId}
                        />
                      )}
                      {isActive && isReviewEnabled(documentFrontmatter ?? undefined) ? (
                        <KnowledgeHubStatusStrip
                          documentId={tab.id}
                          organizationId={tab.workspaceId ?? undefined}
                          status={String(documentFrontmatter?.status || "draft")}
                          verifiedBy={documentFrontmatter?.verified_by as string | undefined}
                          verifiedAt={documentFrontmatter?.verified_at as string | undefined}
                          frontmatter={documentFrontmatter ?? undefined}
                          onStatusChange={async () => {
                            try {
                              const res = await fetchWithRetry(`/api/documents/${tab.id}`)
                              if (res.ok) {
                                const doc = await res.json()
                                setDocumentFrontmatter(doc.frontmatter || {})
                                setDocumentBodyForChangelog(doc.content || "")
                              }
                            } catch { /* ignore */ }
                          }}
                        />
                      ) : null}
                      {isActive && (tab.type === "skill" || documentFrontmatter?.kind === "skill") ? (
                        <SkillDocumentBadge
                          tier={documentFrontmatter?.tier as string | undefined}
                          category={documentFrontmatter?.category as string | undefined}
                        />
                      ) : null}
                      <div ref={tocContentRef} className="flex-1 min-h-0">
                        <DocumentContent
                          documentId={tab.id}
                          documentFilePathFallback={tab.filePath ?? null}
                          documentTabActive={isActive}
                          onAIDocumentSave={handleAIDocumentSave}
                          onAIDocumentCancel={handleAIDocumentCancel}
                          onSendToChat={handleSendToChat}
                          onInternalLinkClick={handleInternalLinkClick}
                          onManualSave={handleManualSave}
                          onManualSaveComplete={completeManualSave}
                          saving={isActive && documentSaveState.saving}
                          isDeleted={isActive && documentSaveState.isDeleted}
                          onFrontmatterChange={handleFrontmatterChange}
                          onEditorModeChange={isActive ? setMarkdownBodyViewMode : undefined}
                          onChangeBannerVisibleChange={
                            isActive ? setChangeBannerVisible : undefined
                          }
                          onDocumentMetaSync={onDocumentMetaSync}
                          driveSyncActivity={isActive ? driveSyncActivity : undefined}
                          driveSyncLabel={isActive ? driveSyncLabel : undefined}
                          driveGdocId={isActive ? driveGdocId : undefined}
                          driveOrganizationId={isActive ? driveOrganizationId : undefined}
                          driveLastSynced={isActive ? driveLastSynced : undefined}
                          driveLastPushed={isActive ? driveLastPushed : undefined}
                          driveLastPulled={isActive ? driveLastPulled : undefined}
                          driveKeepSynced={isActive ? driveKeepSynced : undefined}
                        />
                      </div>
                      {showDocumentToc && tocHeadings.length > 0 && markdownBodyViewMode !== "source" && !isProductBoardDocument && (
                        <CurrentSectionPill
                          headings={tocHeadings}
                          contentRef={tocContentRef as React.RefObject<HTMLElement>}
                          scrollRef={scrollElRef}
                          tocOpen={tocOpen}
                          onToggle={() => setTocOpen((v) => !v)}
                          topOffsetPx={
                            (changeBannerVisible ? 48 : 0) +
                            (isActive && isReviewEnabled(documentFrontmatter ?? undefined)
                              ? DOC_CHROME_STRIP_OFFSET_PX
                              : 0) +
                            (isActive &&
                            (tab.type === "skill" || documentFrontmatter?.kind === "skill")
                              ? DOC_CHROME_STRIP_OFFSET_PX
                              : 0)
                          }
                        />
                      )}
                    </div>
                  </div>
                </div>
              ) : viewMode === "double" ? (
                <PanelGroup direction="horizontal" onLayout={setDocumentPanelSizes}>
                  <Panel defaultSize={documentPanelSizes[0] || 50} minSize={30}>
                    <div className="h-full border-r border-border">
                      <DocumentContent
                        documentId={tab.id}
                        documentFilePathFallback={tab.filePath ?? null}
                        documentTabActive={isActive}
                        onAIDocumentSave={handleAIDocumentSave}
                        onAIDocumentCancel={handleAIDocumentCancel}
                        onSendToChat={handleSendToChat}
                        onInternalLinkClick={handleInternalLinkClick}
                        onManualSave={handleManualSave}
                        onManualSaveComplete={completeManualSave}
                        saving={isActive && documentSaveState.saving}
                        isDeleted={isActive && documentSaveState.isDeleted}
                        onFrontmatterChange={handleFrontmatterChange}
                        onDocumentMetaSync={onDocumentMetaSync}
                        driveSyncActivity={isActive ? driveSyncActivity : undefined}
                        driveSyncLabel={isActive ? driveSyncLabel : undefined}
                        driveGdocId={isActive ? driveGdocId : undefined}
                        driveOrganizationId={isActive ? driveOrganizationId : undefined}
                        driveLastSynced={isActive ? driveLastSynced : undefined}
                        driveLastPushed={isActive ? driveLastPushed : undefined}
                        driveLastPulled={isActive ? driveLastPulled : undefined}
                        driveKeepSynced={isActive ? driveKeepSynced : undefined}
                      />
                    </div>
                  </Panel>
                  <PanelResizeHandle className="w-1 bg-border hover:bg-primary transition-colors relative group">
                    <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-4 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <GripVertical className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </PanelResizeHandle>
                  <Panel defaultSize={documentPanelSizes[1] || 50} minSize={30}>
                    <DocumentContent
                      documentId={tab.id}
                      documentFilePathFallback={tab.filePath ?? null}
                      documentTabActive={isActive}
                      onAIDocumentSave={handleAIDocumentSave}
                      onAIDocumentCancel={handleAIDocumentCancel}
                      onSendToChat={handleSendToChat}
                      onInternalLinkClick={handleInternalLinkClick}
                      onManualSave={handleManualSave}
                      onManualSaveComplete={completeManualSave}
                      saving={isActive && documentSaveState.saving}
                      isDeleted={isActive && documentSaveState.isDeleted}
                      onFrontmatterChange={handleFrontmatterChange}
                      onDocumentMetaSync={onDocumentMetaSync}
                      driveSyncActivity={isActive ? driveSyncActivity : undefined}
                      driveSyncLabel={isActive ? driveSyncLabel : undefined}
                      driveGdocId={isActive ? driveGdocId : undefined}
                      driveOrganizationId={isActive ? driveOrganizationId : undefined}
                      driveLastSynced={isActive ? driveLastSynced : undefined}
                      driveLastPushed={isActive ? driveLastPushed : undefined}
                      driveLastPulled={isActive ? driveLastPulled : undefined}
                      driveKeepSynced={isActive ? driveKeepSynced : undefined}
                    />
                  </Panel>
                </PanelGroup>
              ) : viewMode === "diff" ? (
                <div className="h-full p-6">
                  <DiffViewer />
                </div>
              ) : null
            ) : null}
          </div>
        )
      })
  )

  const shellInfoDockKind = resolveShellInfoDockKind(activeTab, activeTabId)

  return (
    <PanelGroup direction="horizontal" className="flex-1 min-h-0 overflow-hidden">
      <Panel
        defaultSize={
          showInfoPane && shellInfoDockKind
            ? 100 - PANEL_SIZES.DOCUMENT_INFO_DEFAULT
            : 100
        }
        minSize={50}
        className="overflow-hidden relative"
      >
        {renderTabsContent()}
      </Panel>

      {showInfoPane && shellInfoDockKind && activeTab ? (
        <>
          <PanelResizeHandle className="w-px bg-border hover:bg-primary transition-colors" />

          <SecondaryPanel
            ref={infoPanelRef}
            panelType={infoDockMode === "history" ? "document-version-history" : "document-info"}
            panelId={
              infoDockMode === "history"
                ? `document-version-history-${activeTabId}`
                : `document-info-${activeTabId}`
            }
            defaultSize={PANEL_SIZES.DOCUMENT_INFO_DEFAULT}
            minSize={PANEL_SIZES.DOCUMENT_INFO_MIN}
            className="border-l border-border min-w-0 overflow-hidden"
          >
            {shellInfoDockKind === "todo-edit" ? (
              <Suspense fallback={<LazyViewFallback />}>
                <BasecampTodoEditPane
                  todoId={activeTab.integrationData?.todoId || activeTab.id.replace("basecamp-todo-", "")}
                  projectId={activeTab.integrationData?.projectId}
                  organizationId={
                    activeTab.integrationData?.organizationId ?? activeTab.workspaceId ?? undefined
                  }
                  todo={activeTab.integrationData?.todoData || null}
                  todoUrl={
                    activeTab.integrationData?.projectId && (activeTab.integrationData?.todoId || activeTab.id.replace("basecamp-todo-", ""))
                      ? constructBasecampUrl(
                          activeTab.integrationData?.todoId || activeTab.id.replace("basecamp-todo-", ""),
                          activeTab.integrationData.projectId,
                        )
                      : ""
                  }
                  onSave={async () => {
                    window.dispatchEvent(new CustomEvent("basecamp-todo-refresh", {
                      detail: {
                        todoId: activeTab.integrationData?.todoId || activeTab.id.replace("basecamp-todo-", ""),
                        projectId: activeTab.integrationData?.projectId,
                      },
                    }))
                  }}
                  onClose={handleCloseInfoPane}
                />
              </Suspense>
            ) : shellInfoDockKind === "project" ? (
              <Suspense fallback={<LazyViewFallback />}>
                <BasecampProjectInfoPane
                  projectId={activeTab.integrationData?.projectId || activeTab.id.replace("basecamp-project-", "")}
                  onClose={handleCloseInfoPane}
                />
              </Suspense>
            ) : shellInfoDockKind === "nextcloud-document" ? (
              <DocumentInfoDock
                mode={infoDockMode}
                onModeChange={setInfoDockMode}
                onClose={handleCloseInfoPane}
                showHistoryTab={false}
              >
                <NextcloudDocumentInfoPane
                  name={activeTab.title}
                  path={activeTab.integrationData?.nextcloudPath || '/'}
                  fileId={activeTab.integrationData?.nextcloudFileId}
                  organizationId={
                    activeTab.integrationData?.organizationId ?? activeTab.workspaceId ?? undefined
                  }
                />
              </DocumentInfoDock>
            ) : shellInfoDockKind === "drive-preview" && activeTab.integrationData?.driveFileId ? (
              <DocumentInfoDock
                mode={infoDockMode}
                onModeChange={setInfoDockMode}
                onClose={handleCloseInfoPane}
                showHistoryTab
                showCommentsTab
              >
                <DrivePreviewInfoPane
                  mode={infoDockMode}
                  fileId={activeTab.integrationData.driveFileId}
                  organizationId={
                    activeTab.integrationData.organizationId ?? activeTab.workspaceId ?? undefined
                  }
                  name={activeTab.title}
                  mimeType={activeTab.integrationData.driveMimeType ?? ""}
                  webViewLink={activeTab.integrationData.driveWebViewLink}
                />
              </DocumentInfoDock>
            ) : (
              <DocumentInfoDock
                mode={infoDockMode}
                onModeChange={setInfoDockMode}
                onClose={handleCloseInfoPane}
                showHistoryTab={Boolean(
                  activeTabId &&
                    !activeTabId.startsWith("integration-") &&
                    !activeTabId.startsWith("basecamp-"),
                )}
              >
                {infoDockMode === "history" && activeTabId ? (
                  <DocumentVersionHistoryPanel
                    embedded
                    documentId={activeTabId}
                    documentTitle={activeTab?.title}
                    documentPath={activeTab?.filePath ?? null}
                    documentBody={documentBodyForChangelog}
                    reviewEnabled={isReviewEnabled(documentFrontmatter ?? undefined)}
                    onDocumentBodyChange={async (nextBody) => {
                      if (typeof nextBody === "string") {
                        setDocumentBodyForChangelog(nextBody)
                        return
                      }
                      try {
                        const uuid = await resolveDocumentRowId(
                          activeTabId,
                          activeTab?.filePath ?? null,
                          { workspaces },
                        )
                        const res = await fetchWithRetry(`/api/documents/${uuid}`)
                        if (res.ok) {
                          const doc = await res.json()
                          setDocumentBodyForChangelog(doc.content || "")
                        }
                      } catch { /* ignore */ }
                    }}
                    onClose={handleCloseInfoPane}
                  />
                ) : (
                  <DocumentInfoPane
                    hideHeader
                    documentId={activeTabId}
                    documentPath={activeTab?.filePath ?? null}
                    onClose={handleCloseInfoPane}
                    onOpenHistory={() => setInfoDockMode("history")}
                    onShowInBrowse={(filePath) => {
                      window.dispatchEvent(new CustomEvent("show-document-in-browse", {
                        detail: { filePath, documentId: activeTabId, forceRefresh: false, scrollToNode: true },
                      }))
                    }}
                  />
                )}
              </DocumentInfoDock>
            )}
          </SecondaryPanel>
        </>
      ) : null}
    </PanelGroup>
  )
}
